import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { apiUploadImage } from "../../shared/apiClient";
import { getApiBaseUrl } from "../../config/appSettings";
import { CmsPageBuilderRenderer } from "./CmsPageBuilderRenderer";
import {
  buildContentWithMetadata,
  blocksToHtml,
  exportFullHtmlPage,
  parseBlocksFromContent
} from "./cmsPageBuilderSerializer";
import { TableGridEditor, createEmptyTableContent } from "./TableGridEditor";

function id() {
  return Math.random().toString(36).slice(2, 10);
}

function createBlock(type) {
  const base = { id: id(), type, style: {} };
  if (type === "heading") return { ...base, content: { text: "New Heading" } };
  if (type === "text") return { ...base, content: { text: "Write your text..." } };
  if (type === "description") return { ...base, content: { text: "Paste long multi-paragraph description here...\n\nSecond paragraph starts here..." } };
  if (type === "image") return { ...base, content: { src: "", alt: "" } };
  if (type === "button") return { ...base, content: { label: "Read More", href: "#" } };
  if (type === "list") return { ...base, content: { items: ["Item 1", "Item 2"] } };
  if (type === "box") return { ...base, content: { title: "Box Title", text: "Add box content..." }, style: { backgroundColor: "#f8fafc" } };
  if (type === "table") return { ...base, content: createEmptyTableContent() };
  if (type === "columns-2")
    return {
      ...base,
      type: "columns",
      content: {
        columns: 2,
        items: [
          { blocks: [{ type: "heading", content: { text: "Column 1 Heading" } }, { type: "text", content: { text: "Column 1 text" } }] },
          { blocks: [{ type: "heading", content: { text: "Column 2 Heading" } }, { type: "text", content: { text: "Column 2 text" } }] }
        ]
      }
    };
  if (type === "columns-3")
    return {
      ...base,
      type: "columns",
      content: {
        columns: 3,
        items: new Array(3).fill(null).map((_, i) => ({
          blocks: [{ type: "heading", content: { text: `Column ${i + 1} Heading` } }, { type: "text", content: { text: `Column ${i + 1} text` } }]
        }))
      }
    };
  if (type === "columns-4")
    return {
      ...base,
      type: "columns",
      content: {
        columns: 4,
        items: new Array(4).fill(null).map((_, i) => ({
          blocks: [{ type: "heading", content: { text: `Column ${i + 1} Heading` } }, { type: "text", content: { text: `Column ${i + 1} text` } }]
        }))
      }
    };
  return { ...base, content: { leftText: "Left column text", rightText: "Right column text" } };
}

function SortableBlock({ block, selected, onSelect }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <button
      ref={setNodeRef}
      style={style}
      type="button"
      className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${selected ? "border-brand-600 bg-brand-50" : "border-slate-200 bg-white"}`}
      onClick={onSelect}
      {...attributes}
      {...listeners}
    >
      <span className="font-medium">{block.type}</span>
      <span className="ml-2 text-slate-500">{block.content?.text || block.content?.label || block.content?.caption || block.content?.leftText || ""}</span>
    </button>
  );
}

export function CmsPageBuilderEditor({ form, setForm }) {
  const [blocks, setBlocks] = useState(() => parseBlocksFromContent(form.content) ?? [createBlock("heading"), createBlock("text")]);
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState("preview");
  const [linkDraft, setLinkDraft] = useState({ text: "", url: "" });
  const selected = useMemo(() => blocks.find((b) => b.id === selectedId) ?? null, [blocks, selectedId]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    setForm((prev) => ({ ...prev, content: buildContentWithMetadata(blocks), builderJson: JSON.stringify(blocks) }));
  }, [blocks, setForm]);

  const updateSelected = (updater) => {
    if (!selected) return;
    setBlocks((prev) => prev.map((b) => (b.id === selected.id ? updater(b) : b)));
  };

  const toggleStyle = (key, activeValue, fallback = "") => {
    if (!selected) return;
    updateSelected((b) => ({
      ...b,
      style: {
        ...b.style,
        [key]: b.style?.[key] === activeValue ? fallback : activeValue
      }
    }));
  };

  const normalizeCssSize = (value) => {
    const v = String(value ?? "").trim();
    if (!v) return "";
    if (/^\d+(\.\d+)?$/.test(v)) return `${v}px`;
    return v;
  };
  const pxOptions = ["", ...new Array(100).fill(null).map((_, i) => `${i + 1}px`)];
  const fontSizeOptions = ["", ...new Array(100).fill(null).map((_, i) => `${i + 1}px`)];
  const textAlignOptions = ["", "left", "center", "right", "justify"];
  const borderOptions = ["", "none", "1px solid #e2e8f0", "2px solid #cbd5e1", "1px dashed #94a3b8", "2px dashed #64748b"];

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setBlocks((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const deleteSelectedBlock = () => {
    if (!selected) return;
    setBlocks((prev) => prev.filter((b) => b.id !== selected.id));
    setSelectedId(null);
  };

  const appendLinkSyntaxToSelected = () => {
    if (!selected) return;
    const text = (linkDraft.text || "").trim();
    const url = (linkDraft.url || "").trim();
    if (!text || !url) return;
    const syntax = `[${text}](${url})`;
    if (selected.type === "heading" || selected.type === "text") {
      updateSelected((b) => ({ ...b, content: { ...b.content, text: `${b.content?.text || ""} ${syntax}`.trim() } }));
      return;
    }
    if (selected.type === "box") {
      updateSelected((b) => ({ ...b, content: { ...b.content, text: `${b.content?.text || ""} ${syntax}`.trim() } }));
    }
  };

  const htmlOutput = blocksToHtml(blocks);
  const previewHtml = DOMPurify.sanitize(htmlOutput);

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[340px_minmax(0,1fr)]">
      <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 xl:sticky xl:top-4 xl:max-h-[calc(100vh-120px)] xl:overflow-auto">
        <div className="sticky top-0 z-20 -mx-3 border-b border-slate-200 bg-white px-3 pb-2 pt-1">
          <div className="flex flex-wrap gap-2">
          {[
            ["heading", "+ heading"],
            ["text", "+ text"],
            ["description", "+ description"],
            ["image", "+ image"],
            ["button", "+ button"],
            ["list", "+ list"],
            ["box", "+ box"],
            ["table", "+ table"],
            ["columns-2", "+ 2 column section"],
            ["columns-3", "+ 3 column section"],
            ["columns-4", "+ 4 column section"]
          ].map(([type, label]) => (
            <button
              key={type}
              type="button"
              className="rounded-md border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50"
              onClick={() => {
                const b = createBlock(type);
                setBlocks((prev) => [...prev, b]);
                setSelectedId(b.id);
              }}
            >
              {label}
            </button>
          ))}
          </div>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {blocks.map((block) => (
                <SortableBlock
                  key={block.id}
                  block={block}
                  selected={selectedId === block.id}
                  onSelect={() => setSelectedId(block.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {selected && (
          <div className="space-y-2 rounded-lg border border-slate-200 p-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-700">Block Editor</p>
              <button
                type="button"
                className="rounded border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                onClick={deleteSelectedBlock}
              >
                Delete block
              </button>
            </div>
            {selected.type === "heading" || selected.type === "text" || selected.type === "description" ? (
              <>
                <textarea
                  className="w-full rounded border border-slate-200 p-2 text-sm"
                  rows={selected.type === "description" ? 10 : 4}
                  value={selected.content?.text || ""}
                  onChange={(e) => updateSelected((b) => ({ ...b, content: { ...b.content, text: e.target.value } }))}
                />
                <p className="text-[11px] text-slate-500">Link format: <code>[word](https://example.com)</code></p>
              </>
            ) : null}
            {selected.type === "image" ? (
              <>
                <input
                  className="w-full rounded border border-slate-200 p-2 text-sm"
                  placeholder="Image URL"
                  value={selected.content?.src || ""}
                  onChange={(e) => updateSelected((b) => ({ ...b, content: { ...b.content, src: e.target.value } }))}
                />
                <input
                  className="w-full rounded border border-slate-200 p-2 text-sm"
                  placeholder="Alt text"
                  value={selected.content?.alt || ""}
                  onChange={(e) => updateSelected((b) => ({ ...b, content: { ...b.content, alt: e.target.value } }))}
                />
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    className="text-xs"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const r = await apiUploadImage("/api/Uploads/image", file);
                      if (!r.ok) return;
                      const path = r.data?.path || "";
                      const base = getApiBaseUrl();
                      const url = path.startsWith("http") ? path : `${base}${path}`;
                      updateSelected((b) => ({ ...b, content: { ...b.content, src: url } }));
                    }}
                  />
                </div>
              </>
            ) : null}
            {selected.type === "button" ? (
              <>
                <input
                  className="w-full rounded border border-slate-200 p-2 text-sm"
                  placeholder="Button text"
                  value={selected.content?.label || ""}
                  onChange={(e) => updateSelected((b) => ({ ...b, content: { ...b.content, label: e.target.value } }))}
                />
                <input
                  className="w-full rounded border border-slate-200 p-2 text-sm"
                  placeholder="Button URL"
                  value={selected.content?.href || ""}
                  onChange={(e) => updateSelected((b) => ({ ...b, content: { ...b.content, href: e.target.value } }))}
                />
              </>
            ) : null}
            {selected.type === "list" ? (
              <textarea
                className="w-full rounded border border-slate-200 p-2 text-sm"
                rows={5}
                placeholder={"One item per line"}
                value={(selected.content?.items || []).join("\n")}
                onChange={(e) =>
                  updateSelected((b) => ({
                    ...b,
                    content: {
                      ...b.content,
                      items: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean)
                    }
                  }))
                }
              />
            ) : null}
            {selected.type === "box" ? (
              <>
                <input
                  className="w-full rounded border border-slate-200 p-2 text-sm"
                  placeholder="Box title"
                  value={selected.content?.title || ""}
                  onChange={(e) => updateSelected((b) => ({ ...b, content: { ...b.content, title: e.target.value } }))}
                />
                <textarea
                  className="w-full rounded border border-slate-200 p-2 text-sm"
                  rows={4}
                  placeholder="Box text"
                  value={selected.content?.text || ""}
                  onChange={(e) => updateSelected((b) => ({ ...b, content: { ...b.content, text: e.target.value } }))}
                />
                <p className="text-[11px] text-slate-500">Link format: <code>[word](https://example.com)</code></p>
              </>
            ) : null}
            {selected.type === "table" ? (
              <TableGridEditor
                value={selected.content}
                onChange={(content) => updateSelected((b) => ({ ...b, content }))}
              />
            ) : null}
            {selected.type === "section" ? (
              <>
                <textarea
                  className="w-full rounded border border-slate-200 p-2 text-sm"
                  rows={3}
                  placeholder="Left column text"
                  value={selected.content?.leftText || ""}
                  onChange={(e) => updateSelected((b) => ({ ...b, content: { ...b.content, leftText: e.target.value } }))}
                />
                <textarea
                  className="w-full rounded border border-slate-200 p-2 text-sm"
                  rows={3}
                  placeholder="Right column text"
                  value={selected.content?.rightText || ""}
                  onChange={(e) => updateSelected((b) => ({ ...b, content: { ...b.content, rightText: e.target.value } }))}
                />
              </>
            ) : null}
            {selected.type === "columns" ? (
              <>
                {(selected.content?.items || []).map((col, idx) => (
                  <div key={idx} className="space-y-2 rounded border border-slate-200 p-2">
                    <p className="text-xs font-semibold text-slate-700">Column {idx + 1}</p>
                    <div className="flex flex-wrap gap-1">
                      {["heading", "text", "image", "list"].map((t) => (
                        <button
                          key={`${idx}-${t}`}
                          type="button"
                          className="rounded border border-slate-200 px-2 py-1 text-[11px] hover:bg-slate-50"
                          onClick={() =>
                            updateSelected((b) => {
                              const items = [...(b.content?.items || [])];
                              const nextCol = { ...(items[idx] || {}), blocks: [...((items[idx]?.blocks) || []), createBlock(t)] };
                              items[idx] = nextCol;
                              return { ...b, content: { ...b.content, items } };
                            })
                          }
                        >
                          + {t}
                        </button>
                      ))}
                    </div>
                    {((col?.blocks) || []).map((nested, nIdx) => (
                      <div key={`${idx}-${nIdx}`} className="space-y-1 rounded border border-slate-200 p-2">
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] font-medium text-slate-600">{nested.type}</p>
                          <button
                            type="button"
                            className="rounded border border-red-200 px-1.5 py-0.5 text-[11px] text-red-700 hover:bg-red-50"
                            onClick={() =>
                              updateSelected((b) => {
                                const items = [...(b.content?.items || [])];
                                const blocks = [...(items[idx]?.blocks || [])];
                                blocks.splice(nIdx, 1);
                                items[idx] = { ...(items[idx] || {}), blocks };
                                return { ...b, content: { ...b.content, items } };
                              })
                            }
                          >
                            remove
                          </button>
                        </div>
                        {nested.type === "heading" || nested.type === "text" ? (
                          <textarea
                            className="w-full rounded border border-slate-200 p-2 text-xs"
                            rows={3}
                            value={nested.content?.text || ""}
                            onChange={(e) =>
                              updateSelected((b) => {
                                const items = [...(b.content?.items || [])];
                                const blocks = [...(items[idx]?.blocks || [])];
                                blocks[nIdx] = { ...nested, content: { ...(nested.content || {}), text: e.target.value } };
                                items[idx] = { ...(items[idx] || {}), blocks };
                                return { ...b, content: { ...b.content, items } };
                              })
                            }
                          />
                        ) : null}
                        {nested.type === "image" ? (
                          <>
                            <input
                              className="w-full rounded border border-slate-200 p-2 text-xs"
                              placeholder="Image URL"
                              value={nested.content?.src || ""}
                              onChange={(e) =>
                                updateSelected((b) => {
                                  const items = [...(b.content?.items || [])];
                                  const blocks = [...(items[idx]?.blocks || [])];
                                  blocks[nIdx] = { ...nested, content: { ...(nested.content || {}), src: e.target.value } };
                                  items[idx] = { ...(items[idx] || {}), blocks };
                                  return { ...b, content: { ...b.content, items } };
                                })
                              }
                            />
                            <input
                              type="file"
                              accept="image/*"
                              className="w-full text-xs"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const r = await apiUploadImage("/api/Uploads/image", file);
                                if (!r.ok) return;
                                const path = r.data?.path || "";
                                const base = getApiBaseUrl();
                                const url = path.startsWith("http") ? path : `${base}${path}`;
                                updateSelected((b) => {
                                  const items = [...(b.content?.items || [])];
                                  const blocks = [...(items[idx]?.blocks || [])];
                                  blocks[nIdx] = { ...nested, content: { ...(nested.content || {}), src: url } };
                                  items[idx] = { ...(items[idx] || {}), blocks };
                                  return { ...b, content: { ...b.content, items } };
                                });
                              }}
                            />
                          </>
                        ) : null}
                        {nested.type === "list" ? (
                          <textarea
                            className="w-full rounded border border-slate-200 p-2 text-xs"
                            rows={4}
                            placeholder="One item per line"
                            value={(nested.content?.items || []).join("\n")}
                            onChange={(e) =>
                              updateSelected((b) => {
                                const items = [...(b.content?.items || [])];
                                const blocks = [...(items[idx]?.blocks || [])];
                                blocks[nIdx] = {
                                  ...nested,
                                  content: {
                                    ...(nested.content || {}),
                                    items: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean)
                                  }
                                };
                                items[idx] = { ...(items[idx] || {}), blocks };
                                return { ...b, content: { ...b.content, items } };
                              })
                            }
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                ))}
              </>
            ) : null}

            <p className="text-xs font-semibold text-slate-700">Styles</p>
            {(selected.type === "heading" || selected.type === "text" || selected.type === "description" || selected.type === "box") && (
              <div className="space-y-1 rounded border border-slate-200 p-2">
                <p className="text-xs font-medium text-slate-700">Link helper</p>
                <input
                  className="w-full rounded border border-slate-200 p-2 text-xs"
                  placeholder="Link text"
                  value={linkDraft.text}
                  onChange={(e) => setLinkDraft((p) => ({ ...p, text: e.target.value }))}
                />
                <input
                  className="w-full rounded border border-slate-200 p-2 text-xs"
                  placeholder="https://example.com"
                  value={linkDraft.url}
                  onChange={(e) => setLinkDraft((p) => ({ ...p, url: e.target.value }))}
                />
                <button
                  type="button"
                  className="rounded border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50"
                  onClick={appendLinkSyntaxToSelected}
                >
                  Insert Link
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={`rounded border px-2 py-1 text-xs ${selected.style?.fontWeight === "700" ? "border-brand-600 bg-brand-50" : "border-slate-200"}`}
                onClick={() => toggleStyle("fontWeight", "700", "400")}
              >
                B
              </button>
              <button
                type="button"
                className={`rounded border px-2 py-1 text-xs italic ${selected.style?.fontStyle === "italic" ? "border-brand-600 bg-brand-50" : "border-slate-200"}`}
                onClick={() => toggleStyle("fontStyle", "italic", "normal")}
              >
                I
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select
                className="w-full rounded border border-slate-200 p-2 text-xs"
                value={selected.style?.margin || ""}
                onChange={(e) => updateSelected((b) => ({ ...b, style: { ...b.style, margin: e.target.value } }))}
              >
                {pxOptions.map((v) => <option key={`m-${v || "none"}`} value={v}>{v || "Margin"}</option>)}
              </select>
              <select
                className="w-full rounded border border-slate-200 p-2 text-xs"
                value={selected.style?.padding || ""}
                onChange={(e) => updateSelected((b) => ({ ...b, style: { ...b.style, padding: e.target.value } }))}
              >
                {pxOptions.map((v) => <option key={`p-${v || "none"}`} value={v}>{v || "Padding"}</option>)}
              </select>
              <label className="flex items-center gap-2 rounded border border-slate-200 p-2 text-xs">
                <span className="w-20 shrink-0">Text color</span>
                <input
                  type="color"
                  className="h-7 w-10 rounded border border-slate-200 p-0"
                  value={selected.style?.color || "#0f172a"}
                  onChange={(e) => updateSelected((b) => ({ ...b, style: { ...b.style, color: e.target.value } }))}
                />
              </label>
              <label className="flex items-center gap-2 rounded border border-slate-200 p-2 text-xs">
                <span className="w-20 shrink-0">Bg color</span>
                <input
                  type="color"
                  className="h-7 w-10 rounded border border-slate-200 p-0"
                  value={selected.style?.backgroundColor || "#ffffff"}
                  onChange={(e) =>
                    updateSelected((b) => ({
                      ...b,
                      style: { ...b.style, backgroundColor: e.target.value }
                    }))
                  }
                />
              </label>
              <select
                className="w-full rounded border border-slate-200 p-2 text-xs"
                value={selected.style?.fontSize || ""}
                onChange={(e) =>
                  updateSelected((b) => ({
                    ...b,
                    style: { ...b.style, fontSize: normalizeCssSize(e.target.value) }
                  }))
                }
              >
                {fontSizeOptions.map((v) => <option key={`f-${v || "none"}`} value={v}>{v || "Font size"}</option>)}
              </select>
              <input
                className="w-full rounded border border-slate-200 p-2 text-xs"
                placeholder="Font family"
                value={selected.style?.fontFamily || ""}
                onChange={(e) => updateSelected((b) => ({ ...b, style: { ...b.style, fontFamily: e.target.value } }))}
              />
              <select
                className="w-full rounded border border-slate-200 p-2 text-xs"
                value={selected.style?.textAlign || ""}
                onChange={(e) => updateSelected((b) => ({ ...b, style: { ...b.style, textAlign: e.target.value } }))}
              >
                {textAlignOptions.map((v) => <option key={`a-${v || "none"}`} value={v}>{v || "Text align"}</option>)}
              </select>
              <select
                className="w-full rounded border border-slate-200 p-2 text-xs"
                value={selected.style?.borderRadius || ""}
                onChange={(e) => updateSelected((b) => ({ ...b, style: { ...b.style, borderRadius: e.target.value } }))}
              >
                {pxOptions.map((v) => <option key={`r-${v || "none"}`} value={v}>{v || "Border radius"}</option>)}
              </select>
              <select
                className="col-span-2 w-full rounded border border-slate-200 p-2 text-xs"
                value={selected.style?.border || ""}
                onChange={(e) => updateSelected((b) => ({ ...b, style: { ...b.style, border: e.target.value } }))}
              >
                {borderOptions.map((v) => <option key={`b-${v || "none"}`} value={v}>{v || "Border"}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 xl:max-h-[calc(100vh-120px)] xl:overflow-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-slate-800">Output</p>
            <button
              type="button"
              className={`rounded-md border px-2 py-1 text-xs ${viewMode === "preview" ? "border-brand-600 bg-brand-50" : "border-slate-200 hover:bg-slate-50"}`}
              onClick={() => setViewMode("preview")}
            >
              Preview
            </button>
            <button
              type="button"
              className={`rounded-md border px-2 py-1 text-xs ${viewMode === "html" ? "border-brand-600 bg-brand-50" : "border-slate-200 hover:bg-slate-50"}`}
              onClick={() => setViewMode("html")}
            >
              HTML
            </button>
          </div>
          <button
            type="button"
            className="rounded-md border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50"
            onClick={() => {
              const page = exportFullHtmlPage({ title: form.title || "CMS Blog Page", blocks });
              const blob = new Blob([page], { type: "text/html;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${(form.title || "cms-page").replace(/\s+/g, "-").toLowerCase()}.html`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Export Full HTML
          </button>
        </div>
        {viewMode === "preview" ? (
          <div className="rounded-lg border border-slate-200 p-3">
            <CmsPageBuilderRenderer blocks={blocks} />
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="mb-2 text-xs font-semibold text-slate-700">Sanitized HTML</p>
            <textarea
              className="h-64 w-full rounded border border-slate-200 p-2 font-mono text-xs"
              value={previewHtml}
              readOnly
            />
          </div>
        )}
      </div>
    </div>
  );
}

